/**
 * Single source of truth for public marketing copy and site constants.
 *
 * Both the rendered landing page and the JSON-LD @graph read from this module,
 * so structured data can never drift from the copy a visitor actually sees.
 * If you change an FAQ answer here, the schema updates with it.
 *
 * Keep every claim in this file verifiable against the backend. The FAQ answers
 * describe the native API in deployment/api/ and its Google integration.
 */

export const SITE_URL = "https://seo-signals.com";

export const SITE = {
  name: "SEO Signals",
  url: `${SITE_URL}/`,
  title: "SEO Signals: Search Console, GA4 and Bing dashboard",
  // Kept under 160 characters for search result display.
  description:
    "Review Google Search Console, GA4 and Bing Webmaster Tools data in one SEO dashboard. See up to three recommended fixes. Read-only access, free to start.",
  ogImage: `${SITE_URL}/og-image.png`,
  contactEmail: "hello@seo-signals.com",
  // Shown in the footer and used as the schema slogan. Separates this product
  // from similarly named tools.
  descriptor: "Search + Analytics SEO priorities",
  domain: "seo-signals.com",
  // Operator, as stated in the footer, contact page and Terms.
  operator: "Agent Smith",
  locality: "Southampton",
  country: "United Kingdom",
  countryCode: "GB",
};

/**
 * Baked in at build time rather than read from `new Date()` at render time.
 * A runtime year would differ between the prerendered HTML and the hydrating
 * client across a new year boundary, which React reports as a mismatch.
 */
export const BUILD_YEAR = new Date().getFullYear();

/**
 * Direct product definition. Appears within the first 40 to 60 words of the
 * homepage so both search engines and answer engines can extract the category
 * without inferring it from marketing language.
 */
export const PRODUCT_DEFINITION =
  "SEO Signals is an AI SEO assistant that brings Google Search Console, Google Analytics 4 and Bing Webmaster Tools reports together. Review up to three recommended fixes from your Google data.";

// Illustrative situations, not customer results or guaranteed findings.
export const EXAMPLE_INSIGHTS = [
  { id: "low-ctr", icon: "search", tag: "Search clicks", tone: "opportunity",
    headline: "Your page appears. Few people choose the result.",
    body: "A query earns impressions but few clicks. Review its page title and description against the searcher's intent, with Search Console evidence beside the recommendation.",
    action: "Review the title and description" },
  { id: "no-conversion", icon: "trending-down", tag: "Visitor actions", tone: "opportunity",
    headline: "Visits arrive. The next step needs a closer look.",
    body: "An article brings visitors but records few key events. Review its next step and check your event setup before assuming the content is the problem.",
    action: "Review the path to an enquiry" },
  { id: "competing-pages", icon: "alert-triangle", tag: "Overlapping pages", tone: "warning",
    headline: "Two pages appear for the same query.",
    body: "Shared queries are a reason to review your pages, not proof of a problem. Check whether the pages meet different needs before changing content or internal links.",
    action: "Compare the pages and their purpose" },
];

// Fictional sample. No forecast or benchmark-derived uplift is presented.
export const SAMPLE_PREVIEW = {
  label: "Sample data",
  site: "Harborside Bakery",
  window: "Last 28 days",
  state: "Low-CTR opportunity",
  query: "gluten free birthday cake portland",
  metrics: [
    { label: "Avg. position", value: "9" },
    { label: "Impressions", value: "14,200" },
    { label: "Clicks", value: "34" },
    { label: "CTR", value: "0.24%" },
  ],
  readout: "34 clicks from 14,200 impressions gives a 0.24% click-through rate. Review the search result before deciding what to change.",
  action: "Rewrite the page title and meta description",
};

export const STEPS = [
  { n: "01", icon: "link", title: "Connect your sources", body: "Connect Google Search Console and Google Analytics 4 with read-only access. Add Bing Webmaster Tools with your own Bing API key. Your website stays under your control." },
  { n: "02", icon: "sparkles", title: "Review your priorities", body: "See up to three recommended actions from the available data. Review the evidence or ask a follow-up question." },
  { n: "03", icon: "message-square-text", title: "Work through a fix", body: "Make the change on your website and track the fix in SEO Signals. Work through one action at a time." },
];

/**
 * FAQ answers are written to stand alone. Each one names SEO Signals rather
 * than relying on a pronoun, and opens with the direct answer, so an answer
 * engine can quote a single entry without the surrounding page for context.
 *
 * Every claim below is checked against the code:
 *  - Read-only Google scopes and storage are implemented in deployment/api/.
 *  - Supabase stores account and application records; Worker secret storage
 *    holds encrypted provider credentials.
 *  - Google disconnection is available from the account menu. Full account
 *    deletion is handled on request, as described in the privacy policy.
 */
export const FAQS = [
  {
    icon: "eye",
    q: "What data does SEO Signals access?",
    a: "Google sign-in uses your basic profile and email address to create your SEO Signals account. A separate connection asks for read-only access to Search Console and Google Analytics 4 reports, including queries, pages, traffic, engagement and key events. Bing Webmaster Tools is optional and uses your own Bing API key. SEO Signals does not change your website or your connected reporting accounts.",
  },
  {
    icon: "lock",
    q: "Does SEO Signals store my data?",
    a: "Yes. SEO Signals stores account and connection details, settings, conversations and tracked fixes, along with report data needed for its features. Provider credentials are encrypted. Retention depends on the type of data, as explained in the privacy policy. SEO Signals does not sell your Google data. Email hello@seo-signals.com to request deletion.",
  },
  {
    icon: "globe",
    q: "Which sites does SEO Signals support?",
    a: "Start with a website you are authorised to access in Google Search Console. Connect an accessible Google Analytics 4 property to add visitor reports, and optionally connect a verified Bing site with your own Bing API key. You can switch between accessible sites in the dashboard. The reports available depend on your permissions and the data those services return.",
  },
  {
    icon: "badge-check",
    q: "Is SEO Signals free?",
    a: "SEO Signals currently offers free early access without a payment card. You can connect your sources and explore available reports. AI explanations need a configured OpenAI or Anthropic API key and your separate sharing permission; usage through your own key is billed by that provider. The paid plans shown on this page are planned and are not available to purchase yet.",
  },
  { icon: "lock", q: "Do I have to share my reports with an AI provider?", a: "No. Reports remain available without AI sharing. Before an AI explanation, SEO Signals asks for permission to send your question, relevant conversation context and connected report summaries for the selected site to the named provider. You can decline or withdraw permission in AI settings. Google connection and model selection do not grant this permission." },
  { icon: "lock", q: "Can I disconnect my data?", a: "Yes. Choose Disconnect Google from the account menu to stop SEO Signals reading your Search Console and Analytics data and sign out. You can remove your Bing connection in its dashboard section and withdraw AI sharing in AI settings. Disconnection and withdrawal do not automatically delete every stored record; email hello@seo-signals.com for a deletion request." },
  { icon: "sparkles", q: "Will I always receive three fixes?", a: "No. SEO Signals shows up to three priorities when the available data supports them. If there is little data or fewer clear findings, you may see fewer recommendations." },
  { icon: "eye", q: "Will SEO Signals change my website?", a: "No. You review the recommendations and make changes on your website. SEO Signals provides fix tracking and completion controls." },
  { icon: "globe", q: "Do I need an account to try the demo?", a: "No. The demo uses sample data, so you have a way to explore before connecting your own website." },
  { icon: "globe", q: "Is SEO Signals the same as SEO Signal (seosignal.app)?", a: "No. SEO Signals (seo-signals.com) is an independent product and is not affiliated with SEO Signal (seosignal.app) or SEO Signals Lab. SEO Signals reads Google Search Console and Google Analytics 4 together, so its priorities can consider what visitors do after they arrive, not search data alone." },
  { icon: "badge-check", q: "Does SEO Signals guarantee higher rankings?", a: "No. Recommendations help you choose what to work on using the available evidence. Rankings and traffic depend on more than an individual change." },
];
